import {
  CreateBase,
  Form,
  useGetIdentity,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { SaveButton } from "@/components/admin/form";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";

import { CreateSheet } from "../misc/CreateSheet";
import { TaskFormContent } from "./TaskFormContent";
import { categoryForSale, defaultDueDate } from "./taskUtils";
import type { Sale } from "../types";

export interface TaskCreateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The contact the new task is about, when it is created from a Contact page.
   * Pre-fills the optional contact picker, which stays visible and editable so
   * it is obvious what the task will be linked to. LCATOMICZO-31.
   */
  contactId?: Identifier;
}

/**
 * The one task create form, used by every call site: the Action Items and
 * dashboard add buttons, the mobile navigation, and the Contact page section.
 *
 * It picks its own chrome — a centred dialog on desktop, a bottom sheet on
 * mobile — the same switch `Task` makes between `TaskEdit` and `TaskEditSheet`.
 * A three-field form has no business covering a desktop screen (Sam,
 * 2026-09-16), but a bottom sheet is this app's mobile create pattern.
 */
export const TaskCreate = ({
  open,
  onOpenChange,
  contactId,
}: TaskCreateProps) => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const notify = useNotify();
  const isMobile = useIsMobile();
  // Category derives from the assignee's role on submit (no category input).
  // Fetched above the chrome branch: both branches need it for the transform.
  const { data: salesRoster } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });

  if (!identity) return null;

  const record = {
    type: "none",
    due_date: defaultDueDate(),
    sales_id: identity.id,
    ...(contactId != null ? { contact_id: contactId } : {}),
  };

  const transform = (data: any) => ({
    ...data,
    category: categoryForSale(data.sales_id, salesRoster),
  });

  const handleSuccess = () => {
    notify("resources.tasks.added");
    onOpenChange(false);
  };

  const formContent = (
    <TaskFormContent
      showSales
      showContact
      showCategory={false}
      showType={false}
    />
  );

  if (isMobile) {
    return (
      <CreateSheet
        resource="tasks"
        title={
          <h1 className="text-xl font-semibold truncate pr-10">
            {translate("resources.tasks.dialog.create")}
          </h1>
        }
        redirect={false}
        record={record}
        transform={transform}
        // CreateSheet delegates entirely to a supplied onSuccess, so its own
        // notify and redirect stay out of the way.
        mutationOptions={{ onSuccess: handleSuccess }}
        open={open}
        onOpenChange={onOpenChange}
      >
        {formContent}
      </CreateSheet>
    );
  }

  return (
    <CreateBase
      resource="tasks"
      record={record}
      transform={transform}
      redirect={false}
      mutationOptions={{ onSuccess: handleSuccess }}
    >
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="lg:max-w-xl overflow-y-auto max-h-9/10 top-1/20 translate-y-0">
          <Form className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>
                {translate("resources.tasks.dialog.create")}
              </DialogTitle>
            </DialogHeader>
            {formContent}
            <DialogFooter className="w-full justify-end">
              <SaveButton />
            </DialogFooter>
          </Form>
        </DialogContent>
      </Dialog>
    </CreateBase>
  );
};
